import React, { useEffect, useState } from 'react';
import api from '../api';
import { DataGrid } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const columns = (isSuperAdmin, onEdit, onDelete) => [
    { field: 'id', headerName: 'ID', flex: 0.5, minWidth: 70 },
    { field: 'name', headerName: 'School Name', flex: 1, minWidth: 240 },
    { field: 'pastIlgWinner', headerName: 'Past ILG Winner', flex: 0.6, minWidth: 140 },
    { field: 'applicationInProgress', headerName: 'In Progress', flex: 0.6, minWidth: 130 },
    {
        field: 'action', headerName: 'Actions', flex: 1, minWidth: 200, sortable: false, filterable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" onClick={() => onEdit(params.row)}>Edit</Button>
                {isSuperAdmin && !params.row.applicationInProgress && (
                    <Button size="small" color="error" variant="outlined" onClick={() => onDelete(params.row)}>Delete</Button>
                )}
            </Box>
        )
    }
];

const Schools = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchName, setSearchName] = useState('');
    const [filterWinner, setFilterWinner] = useState('any');
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState('');
    const [pastIlgWinner, setPastIlgWinner] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => { load(); checkWhoami(); }, []);

    const checkWhoami = async () => {
        try {
            const res = await api.get('/admin/api/schools/whoami');
            const roles = res.data?.roles || [];
            setIsSuperAdmin(roles.some(r => (r || '').toUpperCase().includes('SUPER_ADMIN')));
        } catch (e) {
            // ignore
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/api/schools');
            const data = res.data || [];
            setItems(data);
        } catch (e) {
            setSnackbar({ open: true, message: 'Failed to load list', severity: 'error' });
        } finally { setLoading(false); }
    };

    const handleAdd = () => { setEditing(null); setName(''); setPastIlgWinner(false); setModalOpen(true); };
    const handleEdit = (it) => { setEditing(it); setName(it.name || ''); setPastIlgWinner(!!it.pastIlgWinner); setModalOpen(true); };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.put(`/admin/api/schools/${editing.id}`, { name, pastIlgWinner });
            } else {
                await api.post('/admin/api/schools', { name, pastIlgWinner });
            }
            setModalOpen(false);
            load();
            setSnackbar({ open: true, message: 'Saved', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
        }
    };

    const handleDelete = async (it) => {
        if (!window.confirm(`Delete school "${it.name}"? This will remove related users and OTPs.`)) return;
        try {
            await api.delete(`/admin/api/schools/${it.id}`);
            load();
            setSnackbar({ open: true, message: 'Deleted', severity: 'success' });
        } catch (e) {
            setSnackbar({ open: true, message: e?.response?.data?.error || 'Delete failed', severity: 'error' });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)', width: '90%', margin: '40px auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h4" component="h2">Schools</Typography>
                <Button variant="contained" onClick={handleAdd}>Add</Button>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, width: '90%', margin: '0 auto 16px' }}>
                <TextField
                    label="Search by name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 240 }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="filter-winner-label">Past ILG Winner</InputLabel>
                    <Select
                        labelId="filter-winner-label"
                        value={filterWinner}
                        label="Past ILG Winner"
                        onChange={(e) => setFilterWinner(e.target.value)}
                    >
                        <MenuItem value={'any'}>Any</MenuItem>
                        <MenuItem value={'yes'}>Yes</MenuItem>
                        <MenuItem value={'no'}>No</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box sx={{ flex: 1, width: '100%' }}>
                <DataGrid
                    rows={items
                        .filter(i => {
                            if (searchName && (!i.name || !String(i.name).toLowerCase().includes(searchName.toLowerCase()))) return false;
                            if (filterWinner === 'yes' && !i.pastIlgWinner) return false;
                            if (filterWinner === 'no' && i.pastIlgWinner) return false;
                            return true;
                        })
                        .map(i => ({ ...i }))}
                    columns={columns(isSuperAdmin, handleEdit, handleDelete)}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    loading={loading}
                    getRowId={(row) => row.id}
                    disableColumnMenu
                    sx={{
                        height: '100%',
                        '& .MuiDataGrid-columnHeaderTitle': { display: 'block', whiteSpace: 'normal', lineHeight: 1.2, fontWeight: 700, textDecoration: 'underline' },
                    }}
                />
            </Box>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
                <DialogTitle>{editing ? 'Edit' : 'Add'} School</DialogTitle>
                <DialogContent>
                    <TextField autoFocus fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <input id="pastIlgWinner" type="checkbox" checked={pastIlgWinner} onChange={(e) => setPastIlgWinner(e.target.checked)} />
                        <label htmlFor="pastIlgWinner">Past ILG Winner (ineligible for ILG)</label>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Schools;
